import React, { useState } from 'react';
import { UserPlus, Trash2, Search, Users, Pencil } from 'lucide-react';
import { getAdapter } from '../../lib/adapters';
import type { FamilyMemberRecord, FamilyTreeRecord } from '../../lib/adapters';
import { useDashboardStore } from '../../store/dashboardStore';
import { MemberPicker } from '../Forms/MemberPicker';

type Gender = 'male' | 'female' | 'other';
type MaritalStatus = 'single' | 'married' | 'divorced' | 'widowed';
type RelType = 'child' | 'spouse' | 'sibling' | 'parent';

const EMPTY_FORM = {
  name: '',
  nickname: '',
  birthDate: '',
  deathDate: '',
  birthPlace: '',
  currentLocation: '',
  profession: '',
  education: '',
  gender: 'male' as Gender,
  maritalStatus: 'single' as MaritalStatus,
  isAlive: true,
  generation: 1,
};

interface MembersManagerProps {
  trees: FamilyTreeRecord[];
}

/**
 * Add / remove family members directly from the admin dashboard.
 * No canvas context menus needed — everything is a form.
 */
export const MembersManager: React.FC<MembersManagerProps> = ({ trees }) => {
  const [selectedTreeId, setSelectedTreeId] = useState<string>(trees[0]?.id ?? '');
  const [members, setMembers] = useState<FamilyMemberRecord[]>([]);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [relType, setRelType] = useState<RelType | ''>('');
  const [relatedMemberId, setRelatedMemberId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState<FamilyMemberRecord | null>(null);

  const loadMembers = async (treeId: string) => {
    if (!treeId) return;
    setLoading(true);
    setError(null);
    try {
      const adapter = getAdapter();
      const data = await adapter.listMembers(treeId);
      setMembers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  // Load members for the selected tree on mount / when the tree changes.
  React.useEffect(() => {
    if (selectedTreeId) loadMembers(selectedTreeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTreeId]);

  const handleTreeSelect = (treeId: string) => {
    setSelectedTreeId(treeId);
    setMembers([]);
    setEditingMember(null);
    setShowForm(false);
  };

  const set = (patch: Partial<typeof form>) => setForm(f => ({ ...f, ...patch }));

  /** Load a member's data into the form for editing. */
  const startEditMember = (m: FamilyMemberRecord) => {
    setEditingMember(m);
    setRelType('');
    setRelatedMemberId(null);
    setForm({
      name: m.name ?? '',
      nickname: m.nickname ?? '',
      birthDate: m.birthDate ?? '',
      deathDate: m.deathDate ?? '',
      birthPlace: m.birthPlace ?? '',
      currentLocation: m.currentLocation ?? '',
      profession: m.profession ?? '',
      education: m.education ?? '',
      gender: m.gender ?? 'male',
      maritalStatus: m.maritalStatus ?? 'single',
      isAlive: m.isAlive ?? true,
      generation: m.generation ?? 1,
    });
    setError(null);
    setSuccess(null);
    setShowForm(true);
  };

  /** Reset the form back to the "add member" state. */
  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setRelType('');
    setRelatedMemberId(null);
    setEditingMember(null);
    setShowForm(false);
  };

  const handleSubmitMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTreeId) {
      setError('Select a tree first.');
      return;
    }
    if (form.name.trim().length < 2) {
      setError('Name must be at least 2 characters.');
      return;
    }
    if (relType && !relatedMemberId) {
      setError('You picked a relationship but no related member. Choose one or clear the relationship.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const adapter = getAdapter();
      const payload = {
        name: form.name.trim(),
        nickname: form.nickname.trim() || undefined,
        gender: form.gender,
        birthDate: form.birthDate || '',
        deathDate: !form.isAlive ? (form.deathDate || undefined) : undefined,
        birthPlace: form.birthPlace.trim() || undefined,
        currentLocation: form.currentLocation.trim() || undefined,
        profession: form.profession.trim() || undefined,
        education: form.education.trim() || undefined,
        isAlive: form.isAlive,
        generation: form.generation,
        maritalStatus: form.maritalStatus,
      };

      if (editingMember) {
        await adapter.updateMember(editingMember.id, payload);
        setSuccess(`Updated ${payload.name}.`);
        setShowForm(false);
        setEditingMember(null);
      } else {
        const member = await adapter.createMember(selectedTreeId, payload);

        // Link the new member to an existing one if a relationship was chosen.
        if (relType && relatedMemberId) {
          const type = relType === 'child' ? 'parent' : relType === 'parent' ? 'child' : relType;
          if (type === 'parent') {
            // New member is the child of relatedMemberId → related is the parent
            await adapter.createRelationship(selectedTreeId, relatedMemberId, member.id, 'parent');
          } else if (type === 'child') {
            // New member is the parent of relatedMemberId → new member is the parent
            await adapter.createRelationship(selectedTreeId, member.id, relatedMemberId, 'parent');
          } else if (type === 'spouse') {
            await adapter.createRelationship(selectedTreeId, member.id, relatedMemberId, 'spouse');
            await adapter.createRelationship(selectedTreeId, relatedMemberId, member.id, 'spouse');
          } else if (type === 'sibling') {
            await adapter.createRelationship(selectedTreeId, member.id, relatedMemberId, 'sibling');
          }
        }

        setSuccess(`Added ${member.name} to the tree.`);
      }

      resetForm();
      loadMembers(selectedTreeId);
      useDashboardStore.getState().fetchTrees(); // refresh member counts
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save member');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = async (member: FamilyMemberRecord) => {
    if (!confirm(`Delete ${member.name} from the tree? This cannot be undone.`)) return;
    setError(null);
    setSuccess(null);
    try {
      const adapter = getAdapter();
      await adapter.deleteMember(member.id);
      setMembers(prev => prev.filter(m => m.id !== member.id));
      setSuccess(`Removed ${member.name}.`);
      useDashboardStore.getState().fetchTrees();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete member');
    }
  };

  const filtered = search.trim()
    ? members.filter(m => m.name.toLowerCase().includes(search.trim().toLowerCase()))
    : members;

  return (
    <div className="space-y-6">
      {/* Tree selector + add button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Tree</label>
          <select
            value={selectedTreeId}
            onChange={e => handleTreeSelect(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-600"
          >
            {trees.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => {
            if (showForm && !editingMember) { setShowForm(false); return; }
            resetForm();
            setShowForm(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-700 text-white text-sm font-semibold rounded-lg hover:bg-green-800 transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Add Member
        </button>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</p>}
      {success && <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2">{success}</p>}

      {/* Add / edit member form */}
      {showForm && (
        <form onSubmit={handleSubmitMember} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">
              {editingMember ? `Edit ${editingMember.name}` : `Add a member to ${trees.find(t => t.id === selectedTreeId)?.name}`}
            </h3>
            <button
              type="button"
              onClick={resetForm}
              className="text-sm text-gray-400 hover:text-gray-600"
            >
              Cancel
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => set({ name: e.target.value })}
              placeholder="e.g. Fatema Begum"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nickname</label>
              <input
                type="text"
                value={form.nickname}
                onChange={e => set({ nickname: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <select
                value={form.gender}
                onChange={e => set({ gender: e.target.value as Gender })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-600"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Birth Date</label>
              <input
                type="date"
                value={form.birthDate}
                onChange={e => set({ birthDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Birth Place</label>
              <input
                type="text"
                value={form.birthPlace}
                onChange={e => set({ birthPlace: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Location</label>
              <input
                type="text"
                value={form.currentLocation}
                onChange={e => set({ currentLocation: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Profession</label>
              <input
                type="text"
                value={form.profession}
                onChange={e => set({ profession: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Education</label>
              <input
                type="text"
                value={form.education}
                onChange={e => set({ education: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status</label>
              <select
                value={form.maritalStatus}
                onChange={e => set({ maritalStatus: e.target.value as MaritalStatus })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-600"
              >
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="widowed">Widowed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Generation</label>
              <input
                type="number"
                min={1}
                value={form.generation}
                onChange={e => set({ generation: parseInt(e.target.value) || 1 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mt-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isAlive}
                  onChange={e => set({ isAlive: e.target.checked })}
                  className="w-4 h-4 text-green-600 rounded"
                />
                Currently alive
              </label>
            </div>
          </div>

          {/* Death date — only when not alive */}
          {!form.isAlive && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Death Date</label>
              <input
                type="date"
                value={form.deathDate}
                onChange={e => set({ deathDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
          )}

          {/* Optional relationship — only when adding a new member */}
          {!editingMember && (
          <div className="border-t border-gray-100 pt-5">
            <h4 className="text-sm font-semibold text-gray-900 mb-1">Link to an existing member <span className="text-gray-400 font-normal">(optional)</span></h4>
            <p className="text-xs text-gray-500 mb-4">Sets the new member's relationship so they appear correctly in the tree.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                <select
                  value={relType}
                  onChange={e => setRelType(e.target.value as RelType | '')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-600"
                >
                  <option value="">None</option>
                  <option value="child">Child of…</option>
                  <option value="spouse">Spouse of…</option>
                  <option value="sibling">Sibling of…</option>
                  <option value="parent">Parent of…</option>
                </select>
              </div>
              <MemberPicker
                members={members}
                value={relatedMemberId}
                onChange={setRelatedMemberId}
                label="Related to"
              />
            </div>
          </div>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-green-700 text-white text-sm font-semibold rounded-lg hover:bg-green-800 transition-colors disabled:opacity-60"
            >
              {saving ? 'Saving…' : editingMember ? 'Save Changes' : 'Add Member'}
            </button>
          </div>
        </form>
      )}

      {/* Member list */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">
            Members <span className="text-gray-400 font-normal">({members.length})</span>
          </h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search members…"
              className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
            />
          </div>
        </div>

        {loading ? (
          <p className="px-4 py-12 text-center text-gray-400">Loading members…</p>
        ) : !selectedTreeId ? (
          <p className="px-4 py-12 text-center text-gray-400">Select a tree to see its members.</p>
        ) : filtered.length === 0 ? (
          <p className="px-4 py-12 text-center text-gray-400">
            {search ? 'No members match your search.' : 'No members in this tree yet. Add the first one!'}
          </p>
        ) : (
          <div className="max-h-[32rem] overflow-y-auto">
            {filtered.map(m => (
              <div key={m.id} className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                    <Users className="w-4 h-4 text-green-700" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{m.name}</p>
                    <p className="text-xs text-gray-500">
                      {m.generation ? `Gen ${m.generation}` : ''}
                      {m.isAlive ? '' : ' · Deceased'}
                      {m.profession ? ` · ${m.profession}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => startEditMember(m)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title={`Edit ${m.name}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteMember(m)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title={`Delete ${m.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
